import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { AutoBreadcrumb } from '@/components/ui/auto-breadcrumb'
import { useAuth } from '@/contexts/auth-context'
import { fetchWithAuth } from '@/lib/api'
import { API_BASE } from '@/config/api'
import { useTheme } from '@/components/providers/theme-provider'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { CurrencySelectionModal } from '@/components/modals/currency-selection-modal'
import { EditProfileModal } from '@/components/modals/edit-profile-modal'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { DeleteConfirmationDialog } from '@/components/ui/delete-confirmation-dialog'
import { toast } from 'sonner'
import {
  User,
  Globe,
  Settings,
  Loader2,
  Mail,
  Edit,
  Moon,
  Sun,
  AlertTriangle,
  Trash2,
} from 'lucide-react'
import type { Asset } from '@/schemas'

export const Route = createFileRoute('/_authenticated/account')({
  component: AccountPage,
})

function AccountPage() {
  const { user, logout } = useAuth()
  const { theme, setTheme, resolvedTheme } = useTheme()
  const [showCurrencyModal, setShowCurrencyModal] = useState(false)
  const [showEditProfileModal, setShowEditProfileModal] = useState(false)
  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false)
  const [baseCurrencyAsset, setBaseCurrencyAsset] = useState<Asset | null>(null)
  const [loadingCurrency, setLoadingCurrency] = useState(false)
  const currentTheme = theme === 'system' ? resolvedTheme : theme

  // Fetch the base currency asset details
  useEffect(() => {
    if (!user?.base_currency_id) {
      setBaseCurrencyAsset(null)
      return
    }

    const fetchBaseCurrency = async () => {
      setLoadingCurrency(true)
      try {
        const response = await fetchWithAuth(
          `${API_BASE}/assets/${user.base_currency_id}`,
          { method: 'GET' },
        )
        if (response.ok) {
          const data = await response.json()
          setBaseCurrencyAsset(data)
        }
      } catch (error) {
        console.error('Error fetching base currency:', error)
      } finally {
        setLoadingCurrency(false)
      }
    }

    fetchBaseCurrency()
  }, [user?.base_currency_id])

  // Get user initials for avatar
  const getUserInitials = (name?: string) => {
    if (!name) return 'U'
    const parts = name.split(' ')
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }

  const handleDeleteAccount = async () => {
    const response = await fetchWithAuth(`${API_BASE}/users/me`, {
      method: 'DELETE',
    })

    if (!response.ok) {
      let message = 'Failed to delete account'
      try {
        const data = await response.json()
        if (data?.error) {
          message = data.error
        }
      } catch {
        // Ignore parsing errors and keep default message
      }
      toast.error(message)
      throw new Error(message)
    }

    toast.success('Account deleted successfully')
    await logout()
  }

  return (
    <div className="flex flex-col h-screen">
      <main className="flex-1 overflow-auto p-4 md:p-6">
        <AutoBreadcrumb />

        <div className="mt-6 space-y-6 max-w-4xl mx-auto">
          {/* Header with Avatar */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <Avatar className="h-20 w-20 border-2 border-primary/10">
                <AvatarFallback className="text-2xl font-semibold bg-gradient-to-br from-primary/20 to-primary/5">
                  {getUserInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <div>
                <h1 className="text-3xl font-bold tracking-tight">
                  {user?.name || 'User'}
                </h1>
                <div className="flex flex-col gap-1 mt-1">
                  {user?.username && (
                    <p className="text-sm text-muted-foreground font-medium">
                      @{user.username}
                    </p>
                  )}
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <p className="text-muted-foreground">
                      {user?.email || 'No email set'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              className="self-start md:self-auto"
              onClick={() => setShowEditProfileModal(true)}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit Profile
            </Button>
          </div>

          <Separator />

          {/* Account Overview Cards */}
          <div className="grid gap-4">
            {/* Profile Information */}
            <Card className="transition-all hover:shadow-md">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle>Profile Information</CardTitle>
                      <CardDescription>Your personal details</CardDescription>
                    </div>
                  </div>
                  <Badge variant="secondary">Active</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b last:border-0">
                  <div className="flex items-center gap-3">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Full Name</p>
                      <p className="text-sm text-muted-foreground">
                        {user?.name || 'Not set'}
                      </p>
                    </div>
                  </div>
                </div>
                {user?.username && (
                  <div className="flex items-center justify-between py-3 border-b last:border-0">
                    <div className="flex items-center gap-3">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">Username</p>
                        <p className="text-sm text-muted-foreground">
                          @{user.username}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between py-3 border-b last:border-0">
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Email Address</p>
                      <p className="text-sm text-muted-foreground">
                        {user?.email || 'Not set'}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

          </div>

          {/* Preferences Section */}
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold tracking-tight">
                Preferences
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Customize your experience
              </p>
            </div>

            {/* Currency Settings */}
            <Card className="transition-all hover:shadow-md">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-blue-500/10">
                    <Globe className="h-5 w-5 text-blue-600 dark:text-blue-500" />
                  </div>
                  <div>
                    <CardTitle>Currency Settings</CardTitle>
                    <CardDescription>
                      Configure your base currency for portfolio display
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg border bg-accent/30">
                  <div className="space-y-1 flex-1">
                    <p className="text-sm font-medium text-muted-foreground">
                      Base Currency
                    </p>
                    {loadingCurrency ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm text-muted-foreground">
                          Loading...
                        </span>
                      </div>
                    ) : baseCurrencyAsset ? (
                      <div className="flex items-center gap-2">
                        <p className="text-lg font-semibold">
                          {baseCurrencyAsset.symbol}
                        </p>
                        <span className="text-sm text-muted-foreground">-</span>
                        <p className="text-sm text-muted-foreground">
                          {baseCurrencyAsset.name}
                        </p>
                      </div>
                    ) : (
                      <p className="text-base text-muted-foreground italic">
                        Not configured
                      </p>
                    )}
                  </div>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => setShowCurrencyModal(true)}
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    {baseCurrencyAsset ? 'Change' : 'Set Currency'}
                  </Button>
                </div>
                <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900">
                  <p className="text-sm text-blue-900 dark:text-blue-100">
                    <strong>Note:</strong> Your base currency is used to display
                    portfolio values, performance metrics, and convert foreign
                    currency holdings.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="transition-all hover:shadow-md">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-indigo-500/10">
                    <Settings className="h-5 w-5 text-indigo-600 dark:text-indigo-500" />
                  </div>
                  <div>
                    <CardTitle>Theme Settings</CardTitle>
                    <CardDescription>
                      Choose how WealthScale looks for you
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Button
                    type="button"
                    variant={currentTheme === 'light' ? 'default' : 'outline'}
                    className="justify-start gap-2"
                    onClick={() => setTheme('light')}
                  >
                    <Sun className="h-4 w-4" />
                    Light Theme
                  </Button>
                  <Button
                    type="button"
                    variant={currentTheme === 'dark' ? 'default' : 'outline'}
                    className="justify-start gap-2"
                    onClick={() => setTheme('dark')}
                  >
                    <Moon className="h-4 w-4" />
                    Dark Theme
                  </Button>
                </div>
                <div className="p-4 rounded-lg bg-muted/30 border">
                  <p className="text-sm text-muted-foreground">
                    Current theme:{' '}
                    <span className="font-medium text-foreground capitalize">
                      {currentTheme}
                    </span>
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-red-200/70 dark:border-red-900/50">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-red-500/10">
                    <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-500" />
                  </div>
                  <div>
                    <CardTitle>Danger Zone</CardTitle>
                    <CardDescription>
                      Permanently delete your account and all related data
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 rounded-lg border border-red-200 dark:border-red-900/60 bg-red-50 dark:bg-red-950/20">
                  <p className="text-sm text-red-900 dark:text-red-100">
                    This deletes your account, portfolios, records, performances,
                    value charts and TWR charts. This action cannot be undone.
                  </p>
                </div>
                <Button
                  variant="destructive"
                  onClick={() => setShowDeleteAccountDialog(true)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete My Account
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        <CurrencySelectionModal
          open={showCurrencyModal}
          onClose={() => setShowCurrencyModal(false)}
          isRequired={false}
        />

        <EditProfileModal
          open={showEditProfileModal}
          onClose={() => setShowEditProfileModal(false)}
        />

        <DeleteConfirmationDialog
          open={showDeleteAccountDialog}
          onOpenChange={setShowDeleteAccountDialog}
          title="Delete account"
          description="Are you sure you want to permanently delete your account?"
          itemName={user?.email || user?.name || 'Current account'}
          onConfirm={handleDeleteAccount}
          confirmButtonText="Delete my account"
        />
      </main>
    </div>
  )
}
